import { z } from 'zod';
import { resolvedOfferCardSchema } from './offer-card.js';
import { contentRowSchema, contentSchemaSchema } from './content-schema.js';
import { easingRefSchema, motionSpecSchema, resolvedMotionSchema, } from './motion-schema.js';
import { validatePremiumComposition, } from './premium-composition.js';
import { animationRef, fontRef, iconGlyphRef, transitionSpecRef, typeStyleRef, } from './registries/index.js';
import { DERIVED_COLOR_ROLES, themeSchema } from './theme.js';
// Helper: a Slot is either a fixed value or a query the synthesizer resolves.
export const slot = (value) => z.discriminatedUnion('source', [
    z.object({ source: z.literal('fixed'), value }),
    z.object({
        source: z.literal('query'),
        query: slotQuerySchema,
        required: z.boolean(),
    }),
]);
// Asset tag — kept as a free-form string since AssetContentTypeTag is owned by
// @borradh-workspace/labels and we don't want a hard dep on the labels package
// in the schema layer. The gate validates against real tags at synthesis time.
const assetTagSchema = z.string().min(1);
export const slotQuerySchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('asset-clips'),
        tag: assetTagSchema,
        count: z.tuple([z.number().int().min(0), z.number().int().min(1)]),
    }),
    z.object({
        kind: z.literal('asset-media'),
        tag: assetTagSchema,
        mediaType: z.enum(['image', 'video']),
    }),
    z.object({
        kind: z.literal('script-text'),
        role: z.enum(['hook', 'body', 'cta', 'disclaimer', 'list']),
        index: z.number().int().min(0).optional(),
    }),
    // A named field of the OWNING template's declared `content` schema. Additive
    // sibling of `script-text`; the role enum stays as sugar.
    z.object({
        kind: z.literal('content'),
        field: z.string().min(1),
    }),
    z.object({
        kind: z.literal('music'),
        mood: z.string().optional(),
        bpm: z.tuple([z.number().min(0), z.number().min(0)]).optional(),
    }),
    z.object({
        kind: z.literal('brand'),
        field: z.enum(['primaryColor', 'logoUrl', 'businessName', 'tagline']),
    }),
]);
// Registry-token Zod schemas are owned by ./registries/*-tokens.ts and
// re-exported from the package barrel. We import them here only so they can
// be used inside this file (in templateMediaTrackSchema etc.); we do NOT
// re-export them or we'd collide with registries/index.ts at the barrel.
export const templateOrientationSchema = z.enum([
    'portrait',
    'landscape',
    'square',
]);
export const blockDurationSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('fixed'), frames: z.number().int().min(1) }),
    z.object({ kind: z.literal('content') }),
    z.object({
        kind: z.literal('fill'),
        weight: z.number().min(0).optional(),
    }),
]);
export const templateDurationSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('fixed'), frames: z.number().int().min(1) }),
    z.object({ kind: z.literal('driven'), by: z.string().min(1) }),
]);
export const mediaSourceSchema = z.object({
    url: z.string().url(),
    mediaType: z.enum(['video', 'image']),
    trimStartFrames: z.number().int().min(0),
    naturalDurationFrames: z.number().int().min(1).optional(),
});
export const mediaFitSchema = z.enum(['cover', 'contain', 'contain-blur']);
const beatSyncedCutsSchema = z.object({
    mode: z.literal('beat-synced'),
    beatsPerEdit: z.number().int().min(1).max(16),
});
const evenCutsSchema = z.object({ mode: z.literal('even') });
const clipLengthCutsSchema = z.object({ mode: z.literal('clip-length') });
// One clip per statement of a sequential overlay (v1 1 clip ↔ 1 line).
const overlaySyncedCutsSchema = z.object({ mode: z.literal('overlay-synced') });
/** The four RULE modes — a spacing, not a set of times. */
export const mediaCutRuleSchema = z.discriminatedUnion('mode', [
    beatSyncedCutsSchema,
    evenCutsSchema,
    clipLengthCutsSchema,
    overlaySyncedCutsSchema,
]);
// One measured cut. `atSec`, not a frame: see MediaCut in template-doc.ts —
// a frame number silently means a different instant at a different fps.
export const mediaCutSchema = z.object({
    atSec: z.number().min(0),
    transition: transitionSpecRef,
});
/**
 * A measured cut LIST. Kept a plain ZodObject (the refinement sits on the inner
 * array, not on the object) so it can still be an option of the discriminated
 * union below and of `templateSpineBlockSchema`.
 */
export const mediaCutListSchema = z.object({
    mode: z.literal('at'),
    at: z
        .array(mediaCutSchema)
        .min(1)
        // Strictly increasing. Two cuts at the same instant would produce a
        // zero-length clip whose transition never gets a frame to run in, and an
        // out-of-order list would silently reorder the edit at compile time.
        .refine((cuts) => cuts.every((c, i) => i === 0 || c.atSec > cuts[i - 1].atSec), { message: 'cut times must be strictly increasing' }),
});
export const mediaCutsSchema = z.discriminatedUnion('mode', [
    beatSyncedCutsSchema,
    evenCutsSchema,
    clipLengthCutsSchema,
    overlaySyncedCutsSchema,
    mediaCutListSchema,
]);
// ── Overlay placement ────────────────────────────────────────────────
// Added by M0 W-F3 so the editor's drag handles have somewhere to write.
// Coordinates are region-relative 0–1; w/h optional (overlay sizes itself if
// omitted). The renderer falls back to a centered default when placement is
// absent so existing templates render unchanged.
export const overlayAnchorSchema = z.enum([
    'top-left',
    'top',
    'top-right',
    'left',
    'center',
    'right',
    'bottom-left',
    'bottom',
    'bottom-right',
]);
export const overlayPlacementSchema = z.object({
    anchor: overlayAnchorSchema,
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    width: z.number().min(0).max(1).optional(),
    height: z.number().min(0).max(1).optional(),
});
// ── Spine blocks ─────────────────────────────────────────────────────
export const templateMediaTrackSchema = z.object({
    kind: z.literal('media-track'),
    id: z.string().min(1),
    duration: blockDurationSchema,
    clips: slot(z.array(mediaSourceSchema)),
    fit: mediaFitSchema.optional(),
    cuts: mediaCutsSchema.optional(),
    // The track-wide treatment, applied to every cut a RULE mode yields. It is
    // meaningless beside `cuts: {mode:'at'}`, whose entries each carry their own,
    // and the TS type (TemplateMediaTrack) makes that pairing unwritable. Zod
    // cannot: this object must stay a plain ZodObject to remain an option of
    // `templateSpineBlockSchema`'s discriminated union, and `.superRefine` would
    // make it a ZodEffects. The compiler therefore throws on the pairing, the
    // same way it throws on `overlay-synced` with no sequential driver.
    transition: transitionSpecRef.optional(),
    // Motion over the whole track — the flare's defocus rides here, because the
    // shot it blurs lives on the spine.
    motion: motionSpecSchema.optional(),
    kenBurnsOnImages: z.boolean().optional(),
    fillMode: z.enum(['loop', 'hold']).optional(),
    // Rotates the clip pool before the cuts resolve — see
    // TemplateMediaTrackBase.clipOffset. Lets the panes of a `split` region show
    // four different shots at once instead of the same one four times.
    clipOffset: z.number().int().min(0).optional(),
});
export const templateSolidSchema = z.object({
    kind: z.literal('solid'),
    id: z.string().min(1),
    duration: blockDurationSchema,
    color: slot(z.string().min(1)),
});
// ── panel-stack ─────────────────────────────────────────────────────
//
// See TemplatePanelStack and ./panel-stack.ts. Only the STACKED arrangement is
// authored — the resolved state is derived and is arithmetically full-bleed, so
// there is no field here that can be set to a value that leaves a seam.
// A frame inset, per side. NEGATIVE is legal and load-bearing: journey-2's
// stack is taller than the frame while stacked and slides down into place, so
// a `min(0)` here would make the measured arrangement unstatable.
const panelStackInsetSchema = z.object({
    top: z.number().min(-1).max(1).optional(),
    right: z.number().min(-1).max(1).optional(),
    bottom: z.number().min(-1).max(1).optional(),
    left: z.number().min(-1).max(1).optional(),
});
export const panelStackStackedSchema = z.object({
    // Panel sizes WHILE STACKED. Absent = the resolved `panels` ratios, i.e. the
    // stack only shrinks. Present, the two states differ and a panel edge can
    // travel outward while its opposite edge travels outward too — which is what
    // journey-2's middle band does and what one ratio list cannot say.
    sizes: z.array(z.number().min(0)).min(2).optional(),
    inset: panelStackInsetSchema.optional(),
    gutter: z.number().min(0).max(1).optional(),
    show: z
        .object({
        x: z.number().min(0).max(1).optional(),
        y: z.number().min(0).max(1).optional(),
    })
        .optional(),
    // Bounded at 8 for the same reason a mask extent is: past that the picture is
    // a texture, and the bound should still catch a stray `zoom: 220`.
    zoom: z.number().min(0).max(8).optional(),
});
export const templatePanelStackSchema = z.object({
    kind: z.literal('panel-stack'),
    id: z.string().min(1),
    duration: blockDurationSchema,
    clip: slot(mediaSourceSchema),
    fit: mediaFitSchema.optional(),
    // At least two: one panel is a plain media-track, and saying so here is
    // cheaper than the reviewer working out why a one-panel stack renders as
    // nothing unusual.
    panels: z.array(z.number().min(0)).min(2),
    axis: z.enum(['h', 'v']).optional(),
    stacked: panelStackStackedSchema,
    holdSec: z.number().min(0),
    resolveSec: z.number().min(0),
    easing: easingRefSchema.optional(),
});
export const templateSpineBlockSchema = z.discriminatedUnion('kind', [
    templateMediaTrackSchema,
    templateSolidSchema,
    templatePanelStackSchema,
]);
// ── Overlay blocks ───────────────────────────────────────────────────
// The seven AUTHORED Theme colour roles.
const themeColorRoleSchema = z.enum([
    'primary',
    'secondary',
    'accent',
    'surface',
    'onSurface',
    'onPrimary',
    'muted',
]);
// Authored roles + the DERIVED ones, computed at compile time (see
// `resolveColorRole`). Used by every colour binding site — text colour, Frame
// background, bar fill — so they can't disagree about what role names exist.
//
// SPREAD from `DERIVED_COLOR_ROLES` rather than listed again. The two were
// hand-kept in step and drifted the moment a third derived role was added:
// every authored end card failed `templateDocSchema` with "expected one of
// primary|…|accentDeep", because the type accepted the new role and the
// validator had never heard of it.
const colorRoleRefSchema = z.enum([
    ...themeColorRoleSchema.options,
    ...DERIVED_COLOR_ROLES,
]);
// Per-element type-style override (v1 parity). Every field optional; set fields
// win over the `style` token's resolved value.
export const typeStyleOverrideSchema = z.object({
    fontSize: z.number().positive().optional(),
    fontWeight: z.number().int().min(100).max(900).optional(),
    letterSpacing: z.number().optional(),
    textTransform: z.enum(['none', 'uppercase']).optional(),
    color: z.string().min(1).optional(),
    colorRole: colorRoleRefSchema.optional(),
    fontStyle: z.enum(['italic', 'normal']).optional(),
    // 'nowrap' keeps the line unbroken (a fixed-width band label). Absent =
    // 'normal', so every existing element wraps exactly as before.
    whiteSpace: z.enum(['normal', 'nowrap', 'pre-line']).optional(),
    // Unitless CSS line-height; absent → the renderer's 1.25 default.
    lineHeight: z.number().positive().optional(),
    strokeWidth: z.number().min(0).optional(),
    strokeColor: z.string().min(1).optional(),
    textShadow: z.string().min(1).optional(),
    // Per-element font face override (resolved to a family string at Phase B).
    fontRef: fontRef.optional(),
});
const textContainerSchema = z.enum(['none', 'pill', 'button', 'plate']);
// ── Motion (docs/plans/v2-motion-primitive.md §2) ────────────────────
//
// Defined in ./motion-schema.ts (theme.ts needs `motionTrackSchema` and this
// module already imports theme.ts) and re-exported here so every existing
// import path keeps working.
export { easingRefSchema, exitRefSchema, indexedAnimationRef, motionAnchorSchema, motionChannelSchema, motionSpecSchema, motionTrackSchema, resolvedMotionSchema, } from './motion-schema.js';
const listElementBase = z.object({
    style: typeStyleRef,
    styleOverride: typeStyleOverrideSchema.optional(),
    container: textContainerSchema.optional(),
    entrance: animationRef,
    // One field, one shape, on every animatable thing. Subsumes the ad-hoc
    // `entrance` (+ duration, + delay) groups; those stay for back-compat and
    // are normalised into this by `normalizeMotion` at the top of Phase B.
    motion: motionSpecSchema.optional(),
});
// Where an overlay starts on the master timeline (see OverlayStart in
// template-doc). `from-end` anchors the overlay to finish at the master end.
// 'sequential' | 'from-end' | an ABSOLUTE point on the master (see OverlayStart).
// `fraction` is bounded to 0-1; `frame` only to >= 0, because the master length
// is not known at parse time — the compiler clamps it.
const overlayStartSchema = z.union([
    z.enum(['sequential', 'from-end']),
    z.object({ at: z.literal('frame'), value: z.number().int().min(0) }),
    z.object({ at: z.literal('fraction'), value: z.number().min(0).max(1) }),
]);
// An overlay declaring it belongs to a media SEGMENT rather than sitting above
// it (see OverlayRidesWith in template-doc). The named track must be a
// media-track in the same leaf's spine; the compiler throws if it isn't.
const overlayRidesWithSchema = z.object({ track: z.string().min(1) });
export const templateStaggeredListSchema = z.object({
    kind: z.literal('staggered-list'),
    id: z.string().min(1),
    duration: blockDurationSchema,
    lead: listElementBase.extend({ text: slot(z.string()) }).optional(),
    items: listElementBase.extend({
        texts: slot(z.array(z.string())),
        // Optional small label rendered above every item (e.g. "IMPROVES:").
        kicker: z
            .object({
            text: slot(z.string()),
            style: typeStyleRef,
            styleOverride: typeStyleOverrideSchema.optional(),
        })
            .optional(),
    }),
    trail: listElementBase.extend({ text: slot(z.string()) }).optional(),
    stagger: z.object({
        // 0 = no stagger (all elements appear together — static "just show" lists).
        beatsPerItem: z.number().int().min(0).max(16),
    }),
    reveal: z.enum(['accumulate', 'sequential']).optional(),
    numbered: z.boolean().optional(),
    align: z.enum(['top', 'center', 'bottom']).optional(),
    hAlign: z.enum(['left', 'center', 'right']).optional(),
    placement: overlayPlacementSchema.optional(),
    start: overlayStartSchema.optional(),
    ridesWith: overlayRidesWithSchema.optional(),
});
// ── text (overlay) ──────────────────────────────────────────────────
export const templateTextSchema = z.object({
    kind: z.literal('text'),
    id: z.string().min(1),
    text: slot(z.string()),
    style: typeStyleRef,
    styleOverride: typeStyleOverrideSchema.optional(),
    animation: z.object({
        entrance: animationRef,
        entranceDurationFrames: z.number().int().min(1).optional(),
        entranceDelayFrames: z.number().int().min(0).optional(),
    }),
    motion: motionSpecSchema.optional(),
    placement: overlayPlacementSchema.optional(),
    duration: blockDurationSchema,
    container: textContainerSchema.optional(),
    start: overlayStartSchema.optional(),
    ridesWith: overlayRidesWithSchema.optional(),
});
// ── media-overlay (overlay) ─────────────────────────────────────────
const mediaOverlayCornerEnum = z.enum(['tl', 'tr', 'bl', 'br']);
const templateMediaOverlayPlacementSchema = z.union([
    z.literal('full-bleed'),
    z.object({
        kind: z.literal('corner'),
        corner: mediaOverlayCornerEnum,
        sizeRatio: z.number().min(0).max(1),
    }),
    z.object({
        kind: z.literal('rect'),
        x: z.number().min(0).max(1),
        y: z.number().min(0).max(1),
        w: z.number().min(0).max(1),
        h: z.number().min(0).max(1),
    }),
]);
export const templateMediaOverlaySchema = z.object({
    kind: z.literal('media-overlay'),
    id: z.string().min(1),
    clip: slot(mediaSourceSchema),
    placement: templateMediaOverlayPlacementSchema,
    fit: mediaFitSchema,
    kenBurns: z
        .object({
        from: z.enum(['center', 'left', 'right']),
        zoomFrom: z.number().min(0),
        zoomTo: z.number().min(0),
    })
        .optional(),
    motion: motionSpecSchema.optional(),
    label: z
        .object({
        text: slot(z.string()),
        style: typeStyleRef,
        styleOverride: typeStyleOverrideSchema.optional(),
        corner: mediaOverlayCornerEnum,
    })
        .optional(),
    duration: blockDurationSchema,
    start: overlayStartSchema.optional(),
    ridesWith: overlayRidesWithSchema.optional(),
});
// ── rect (overlay) ──────────────────────────────────────────────────
//
// The smallest block in the engine: a placed rectangle of one colour. It exists
// because nothing else could be a BAR — `solid` is spine-only with no placement
// or motion, and `info-card` paints its background full-bleed when placed. See
// TemplateRect for the full account.
export const templateRectSchema = z.object({
    kind: z.literal('rect'),
    id: z.string().min(1),
    duration: blockDurationSchema,
    color: slot(z.string().min(1)),
    // Bind the fill to a theme role. See TemplateRect.colorRole.
    colorRole: colorRoleRefSchema.optional(),
    radius: z.number().min(0).max(1).optional(),
    entrance: z.object({
        animation: animationRef,
        entranceDurationFrames: z.number().int().min(1).optional(),
    }),
    motion: motionSpecSchema.optional(),
    placement: overlayPlacementSchema.optional(),
    start: overlayStartSchema.optional(),
    ridesWith: overlayRidesWithSchema.optional(),
});
// ── light-rays (overlay) ────────────────────────────────────────────
//
// A shaft of many fine parallel rays. See TemplateLightRays for the measurement
// this exists to express and why it is a block rather than a brightness track.
//
// The bounds are the interesting part. `count` stops at 400 because the pitch
// is box-width/count and a 1080-wide box at 400 rays is already 2.7px per ray —
// past that the shaft aliases against the pixel grid and flickers frame to
// frame, which looks like a bug in the renderer rather than a choice in the
// template. `angle` stops either side of vertical because a ray past +-90 is
// the same ray with its rays reversed, so the wider range would only add two
// spellings of every shaft.
export const templateLightRaysSchema = z.object({
    kind: z.literal('light-rays'),
    id: z.string().min(1),
    duration: blockDurationSchema,
    count: z.number().int().min(1).max(400),
    angle: z.number().min(-90).max(90).optional(),
    thickness: z.number().min(0.01).max(1).optional(),
    softness: z.number().min(0).max(1).optional(),
    feather: z.number().min(0).max(0.5).optional(),
    color: slot(z.string().min(1)),
    intensity: z.number().min(0).max(1).optional(),
    blend: z.enum(['screen', 'plus-lighter']).optional(),
    entrance: z.object({
        animation: animationRef,
        entranceDurationFrames: z.number().int().min(1).optional(),
    }),
    motion: motionSpecSchema.optional(),
    placement: overlayPlacementSchema.optional(),
    start: overlayStartSchema.optional(),
    ridesWith: overlayRidesWithSchema.optional(),
});
// ── info-card (overlay) ─────────────────────────────────────────────
const infoCardBackgroundSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('solid'), color: z.string().min(1) }),
    z.object({
        kind: z.literal('gradient'),
        from: z.string().min(1),
        to: z.string().min(1),
        angle: z.number().optional(),
    }),
]);
export const templateInfoCardSchema = z.object({
    presentation: z.literal('offer').optional(),
    kind: z.literal('info-card'),
    id: z.string().min(1),
    layout: z.enum(['centered', 'left-aligned', 'stacked']),
    headline: z
        .object({ text: slot(z.string()), style: typeStyleRef })
        .optional(),
    items: z
        .object({ texts: slot(z.array(z.string())), style: typeStyleRef })
        .optional(),
    price: z
        .object({
        value: slot(z.string()),
        currency: slot(z.string()),
        style: typeStyleRef,
    })
        .optional(),
    cta: z
        .object({
        text: slot(z.string()),
        url: slot(z.string()).optional(),
        style: typeStyleRef,
    })
        .optional(),
    logo: z
        .object({
        url: slot(z.string()),
        position: z.enum(['top', 'bottom']),
        size: z.enum(['badge', 'hero']).optional(),
        widthRatio: z.number().min(0).max(1).optional(),
    })
        .optional(),
    background: infoCardBackgroundSchema,
    entrance: z.object({
        animation: animationRef,
        entranceDurationFrames: z.number().int().min(1).optional(),
    }),
    motion: motionSpecSchema.optional(),
    duration: blockDurationSchema,
    placement: overlayPlacementSchema.optional(),
    start: overlayStartSchema.optional(),
    ridesWith: overlayRidesWithSchema.optional(),
});
// ── group (overlay) — recursive node tree ───────────────────────────
//
// See TemplateNode in ./template-doc.ts for the model rationale. The binding
// primitive is `Indexed<T>` (`{ byIndex: [...] }` → `xs[index % xs.length]`)
// plus the `item` / `index` content sources, both scoped to the enclosing
// `repeat`.
/**
 * `T | { byIndex: T[] }` — the one per-index mechanism in the model.
 *
 * `.strict()` so that authoring a `base` on a STRING cycle (where merging is
 * meaningless — see `indexedPartial`) is a loud parse error rather than a
 * silently dropped key.
 */
const indexed = (inner) => z.union([inner, z.object({ byIndex: z.array(inner).min(1) }).strict()]);
/**
 * `indexed`, plus an optional shared `base` that every entry is merged over.
 * Offered only for the OBJECT payloads (Frame, TypeStyleOverride) — merging
 * two strings is just replacement, so `base` there would never do anything.
 */
const indexedPartial = (inner) => z.union([
    inner,
    z.object({ byIndex: z.array(inner).min(1), base: inner.optional() }),
]);
export const frameSchema = z.object({
    background: z.string().min(1).optional(),
    backgroundRole: colorRoleRefSchema.optional(),
    // Prefer the org's logo scrim over this frame's own background. See
    // Frame.adoptLogoScrim.
    adoptLogoScrim: z.literal(true).optional(),
    // Explicit px size on the design baseline; absent = size to content.
    width: z.number().min(0).optional(),
    height: z.number().min(0).optional(),
    radius: z.number().min(0).optional(),
    borderColor: z.string().min(1).optional(),
    borderWidth: z.number().min(0).optional(),
    center: z.boolean().optional(),
    paddingX: z.number().min(0).optional(),
    paddingY: z.number().min(0).optional(),
    shadow: z.string().min(1).optional(),
    tail: z.enum(['bottom-left', 'none']).optional(),
});
export const nodeContentSchema = z.discriminatedUnion('source', [
    z.object({ source: z.literal('slot'), slot: slot(z.string()) }),
    z.object({ source: z.literal('item') }),
    // $item.<field> — a named member of a RECORD row. Like `item` it carries no
    // query of its own: the ask lives on the enclosing repeat's `over`.
    z.object({ source: z.literal('field'), field: z.string().min(1) }),
    z.object({
        source: z.literal('index'),
        offset: z.number().int().optional(),
        // Wrap the digits so a badge can read "1." / "STEP 1" without a second
        // text node. Both default to ''.
        prefix: z.string().optional(),
        suffix: z.string().optional(),
    }),
    z.object({
        source: z.literal('literal'),
        text: indexed(z.string()),
    }),
]);
const nodeAlignSchema = z.enum(['start', 'center', 'end', 'stretch']);
// Main-axis distribution. Separate from `align` because 'space-between' has no
// cross-axis meaning (CSS align-items has no such value).
const nodeJustifySchema = z.enum([...nodeAlignSchema.options, 'space-between']);
const nodeWidthSchema = z.enum(['fill', 'hug']);
/** Per-row, time-varying appearance for a `repeat` (see NodeStateStyle). */
export const nodeStateStyleSchema = z.object({
    styleOverride: typeStyleOverrideSchema.optional(),
    container: frameSchema.optional(),
    // Element opacity for the row, applied to the ROW ROOT only so it cannot
    // compound down the subtree.
    opacity: z.number().min(0).max(1).optional(),
});
const stackKnobsShape = {
    direction: z.enum(['v', 'h']),
    gap: z.number().min(0).optional(),
    width: nodeWidthSchema.optional(),
    align: nodeAlignSchema.optional(),
    justify: nodeJustifySchema.optional(),
    container: indexedPartial(frameSchema).optional(),
    entrance: animationRef.optional(),
    entranceDurationFrames: z.number().int().min(1).optional(),
    motion: motionSpecSchema.optional(),
};
export const templateTextNodeSchema = z.object({
    kind: z.literal('text'),
    content: nodeContentSchema,
    style: typeStyleRef,
    styleOverride: indexedPartial(typeStyleOverrideSchema).optional(),
    container: indexedPartial(frameSchema).optional(),
    align: z.enum(['left', 'center', 'right']).optional(),
    entrance: animationRef.optional(),
    entranceDurationFrames: z.number().int().min(1).optional(),
    motion: motionSpecSchema.optional(),
});
// A vector glyph from the engine's closed registry. `glyph` is Indexed so ONE
// repeat cycles heart / bubble / plane the same way it cycles a verb.
export const templateIconNodeSchema = z.object({
    kind: z.literal('icon'),
    glyph: indexed(iconGlyphRef),
    size: z.number().positive().optional(),
    color: indexed(z.string().min(1)).optional(),
    colorRole: colorRoleRefSchema.optional(),
    container: indexedPartial(frameSchema).optional(),
    entrance: animationRef.optional(),
    entranceDurationFrames: z.number().int().min(1).optional(),
    motion: motionSpecSchema.optional(),
});
// A soft radial light. See LightNode — it exists because a lens flare is a
// light that MOVES, and nothing in the vocabulary painted a falloff.
export const templateLightNodeSchema = z.object({
    kind: z.literal('light'),
    color: indexed(z.string().min(1)),
    size: z.number().positive().optional(),
    falloff: z.number().positive().max(2).optional(),
    intensity: z.number().min(0).max(1).optional(),
    entrance: animationRef.optional(),
    entranceDurationFrames: z.number().int().min(1).optional(),
    motion: motionSpecSchema.optional(),
});
// The org's own MARK, from a URL. See LogoNode — `icon` draws a token from a
// CLOSED registry and can never be this clinic's lockup, and setting a lockup
// as type "can only ever be wrong".
export const templateLogoNodeSchema = z.object({
    kind: z.literal('logo'),
    url: slot(z.string()),
    width: z.number().positive().max(1).optional(),
    /**
     * A cap on the mark's HEIGHT, as a fraction of the frame height.
     *
     * WHY A SECOND CONSTRAINT. `width` alone sizes every mark to the same width
     * and lets the height fall out of the artwork's aspect, so perceived size
     * tracks 1/aspect: at one width a 3.3:1 wordmark is a third the height of a
     * 1:1 roundel and reads as a third the size. Real customer logos run from
     * 0.71:1 to over 5:1, so one width cannot serve them — either the lockups are
     * tiny or the roundels are enormous.
     *
     * With both, the width becomes a budget a wide lockup can actually spend
     * while the cap holds a square mark to a sane height. The renderer applies it
     * as `maxWidth` + `maxHeight` with `objectFit: contain`, so the artwork's own
     * aspect decides WHICH limit binds and nothing needs to know the aspect ahead
     * of time.
     *
     * Absent = no cap, which is the previous behaviour exactly.
     */
    maxHeight: z.number().positive().max(1).optional(),
    entrance: animationRef.optional(),
    entranceDurationFrames: z.number().int().min(1).optional(),
    motion: motionSpecSchema.optional(),
});
export const templateBarNodeSchema = z.object({
    kind: z.literal('bar'),
    progress: z.union([z.literal('elapsed'), slot(z.number().min(0).max(1))]),
    height: z.number().positive().optional(),
    radius: z.number().min(0).optional(),
    trackColor: z.string().min(1).optional(),
    fillColor: indexed(z.string().min(1)).optional(),
    fillColorRole: colorRoleRefSchema.optional(),
    glow: z.boolean().optional(),
    tipDot: z.boolean().optional(),
    container: indexedPartial(frameSchema).optional(),
    entrance: animationRef.optional(),
    entranceDurationFrames: z.number().int().min(1).optional(),
    motion: motionSpecSchema.optional(),
});
export const templateNodeSchema = z.lazy(() => z.discriminatedUnion('kind', [
    templateTextNodeSchema,
    templateIconNodeSchema,
    templateLogoNodeSchema,
    templateBarNodeSchema,
    templateLightNodeSchema,
    z.object({
        kind: z.literal('stack'),
        ...stackKnobsShape,
        children: z.array(templateNodeSchema),
    }),
    z.object({
        kind: z.literal('repeat'),
        over: z.union([
            slot(z.array(z.string())),
            slot(z.array(contentRowSchema)),
        ]),
        child: templateNodeSchema,
        direction: z.enum(['v', 'h']).optional(),
        gap: z.number().min(0).optional(),
        width: nodeWidthSchema.optional(),
        align: nodeAlignSchema.optional(),
        justify: nodeJustifySchema.optional(),
        container: indexedPartial(frameSchema).optional(),
        stagger: z
            .object({ beatsPerItem: z.number().int().min(0).max(16) })
            .optional(),
        reveal: z.enum(['accumulate', 'sequential']).optional(),
        active: nodeStateStyleSchema.optional(),
        past: nodeStateStyleSchema.optional(),
    }),
]));
export const templateGroupSchema = z.object({
    kind: z.literal('group'),
    id: z.string().min(1),
    duration: blockDurationSchema,
    root: templateNodeSchema,
    placement: overlayPlacementSchema.optional(),
    start: overlayStartSchema.optional(),
    ridesWith: overlayRidesWithSchema.optional(),
});
export const templateOverlayBlockSchema = z.discriminatedUnion('kind', [
    templateStaggeredListSchema,
    templateTextSchema,
    templateMediaOverlaySchema,
    templateInfoCardSchema,
    templateRectSchema,
    templateLightRaysSchema,
    templateGroupSchema,
]);
// ── Region tree (recursive) ──────────────────────────────────────────
const leafRegionSchema = z.object({
    kind: z.literal('leaf'),
    id: z.string().min(1),
    spine: z.array(templateSpineBlockSchema),
    overlays: z.array(templateOverlayBlockSchema),
});
export const templateRegionSchema = z.lazy(() => z.discriminatedUnion('kind', [
    leafRegionSchema,
    z.object({
        kind: z.literal('split'),
        axis: z.enum(['h', 'v']),
        children: z.array(z.object({
            ratio: z.number().min(0),
            region: templateRegionSchema,
        })),
    }),
]));
// ── Globals ──────────────────────────────────────────────────────────
export const musicSelectionSchema = z.object({
    trackId: z.string().min(1),
    volume: z.number().min(0).max(1),
});
export const narrationConfigSchema = z.discriminatedUnion('source', [
    z.object({
        source: z.literal('tts'),
        fromScript: z.literal(true),
        voice: z.string().optional(),
    }),
    z.object({ source: z.literal('clip'), clipRef: z.string().min(1) }),
]);
export const templateGlobalsSchema = z.object({
    audio: z.object({
        narration: narrationConfigSchema.optional(),
        music: slot(musicSelectionSchema).optional(),
    }),
    captions: z
        .object({ from: z.literal('narration'), style: typeStyleRef })
        .optional(),
});
// ── TemplateDoc root ─────────────────────────────────────────────────
export const templateDocSchema = z.object({
    id: z.string().min(1),
    // What kind of idea this format can tell. See TemplateDoc.covers.
    covers: z.string().min(1).optional(),
    // How the fields COMPOSE into one sentence. See TemplateDoc.structure.
    structure: z.string().min(1).optional(),
    schemaVersion: z.literal(2),
    aspectRatios: z.array(templateOrientationSchema).min(1),
    duration: templateDurationSchema,
    root: templateRegionSchema,
    globals: templateGlobalsSchema,
    // Optional and unread for now — declared-content §3.1 keeps this additive so
    // every existing schemaVersion: 2 doc stays valid.
    content: contentSchemaSchema.optional(),
});
// ═════════════════════════════════════════════════════════════════════
// RenderDoc — Zod mirrors of the resolved shapes in ./render-doc.ts.
//
// These exist alongside the TS interfaces because (a) the worker validates the
// RenderDoc it persists, and (b) the AI-authoring loop in §15.3 wants a
// machine-readable schema to repair against. They are intentionally
// *renderer-agnostic*: no Remotion types here, no asset-aware helpers.
// ═════════════════════════════════════════════════════════════════════
export const renderOrientationSchema = templateOrientationSchema;
export const resolvedDimensionsSchema = z.object({
    width: z.number().int().positive(),
    height: z.number().int().positive(),
});
// ResolvedTypeStyle — Phase B compile-time output. Carries concrete font/size/
// weight/colour values; the renderer never looks up tokens at render time.
export const resolvedTypeStyleSchema = z.object({
    fontFamily: z.string().min(1),
    fontSize: z.number().positive(),
    fontWeight: z.number().int().min(100).max(900),
    letterSpacing: z.number(),
    textTransform: z.enum(['none', 'uppercase']),
    color: z.string().min(1),
    fontStyle: z.enum(['italic', 'normal']).optional(),
    lineHeight: z.number().positive().optional(),
    strokeWidth: z.number().min(0).optional(),
    strokeColor: z.string().min(1).optional(),
    textShadow: z.string().min(1).optional(),
    whiteSpace: z.enum(['normal', 'nowrap', 'pre-line']).optional(),
    // Set by the compiler when the template authored `color` / `colorRole` on
    // this element. Container chrome defers to it instead of forcing contrast.
    colorIsExplicit: z.boolean().optional(),
});
export const resolvedOverlayPlacementSchema = z.object({
    anchor: overlayAnchorSchema,
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    width: z.number().min(0).max(1).optional(),
    height: z.number().min(0).max(1).optional(),
});
export const resolvedMediaClipSchema = z.object({
    id: z.string().min(1),
    // Source BRollClipConfig.assetId — see ResolvedMediaClip.sourceAssetId.
    sourceAssetId: z.string().min(1).optional(),
    url: z.string().min(1),
    mediaType: z.enum(['video', 'image']),
    trimStartFrames: z.number().int().min(0),
    naturalDurationFrames: z.number().int().min(1).optional(),
    startFrame: z.number().int().min(0),
    durationInFrames: z.number().int().min(1),
    // The same shot continuing across a loop seam, not a cut — see
    // ResolvedMediaClip.continuesPrevious. Anything reasoning about CUTS must
    // skip these.
    continuesPrevious: z.boolean().optional(),
    transition: transitionSpecRef.optional(),
    // Overlay ids from the owning leaf that this clip carries into the next
    // clip's transition (see ResolvedMediaClip.carriedOverlayIds).
    carriedOverlayIds: z.array(z.string().min(1)).optional(),
});
export const resolvedTextElementSchema = z.object({
    id: z.string().min(1),
    text: z.string(),
    typeStyle: resolvedTypeStyleSchema,
    container: z.enum(['none', 'pill', 'button', 'plate']).optional(),
    entrance: animationRef,
    entranceFrame: z.number().int().min(0),
    entranceDurationFrames: z.number().int().min(1).optional(),
    /**
     * Local frame at which the element leaves. Absent = holds to the block end.
     * An `{ at: 'end' }` motion track resolves against this; a list element had
     * no notion of its own end before, so exits were impossible here.
     */
    exitFrame: z.number().int().min(0).optional(),
    motion: resolvedMotionSchema.optional(),
    kicker: z
        .object({ text: z.string(), typeStyle: resolvedTypeStyleSchema })
        .optional(),
});
const resolvedBlockBaseShape = {
    id: z.string().min(1),
    startFrame: z.number().int().min(0),
    durationInFrames: z.number().int().min(1),
    // Sequence extension for end-anchored motion. See ResolvedBlockBase.
    motionTailFrames: z.number().int().min(1).optional(),
};
export const resolvedMediaTrackBlockSchema = z.object({
    kind: z.literal('media-track'),
    ...resolvedBlockBaseShape,
    clips: z.array(resolvedMediaClipSchema),
    fit: mediaFitSchema.optional(),
    motion: resolvedMotionSchema.optional(),
});
export const resolvedSolidBlockSchema = z.object({
    kind: z.literal('solid'),
    ...resolvedBlockBaseShape,
    color: z.string().min(1),
});
// `holdSec`/`resolveSec` have become FRAMES of the composition actually being
// built — see TemplatePanelStack on why the authored form is seconds.
export const resolvedPanelStackBlockSchema = z.object({
    kind: z.literal('panel-stack'),
    ...resolvedBlockBaseShape,
    clip: resolvedMediaClipSchema,
    fit: mediaFitSchema.optional(),
    panels: z.array(z.number().min(0)).min(2),
    axis: z.enum(['h', 'v']),
    stacked: panelStackStackedSchema,
    holdFrames: z.number().int().min(0),
    resolveFrames: z.number().int().min(0),
    easing: easingRefSchema.optional(),
});
export const resolvedRectBlockSchema = z.object({
    kind: z.literal('rect'),
    ...resolvedBlockBaseShape,
    color: z.string().min(1),
    radius: z.number().min(0).max(1).optional(),
    entrance: animationRef,
    entranceDurationFrames: z.number().int().min(1).optional(),
    motion: resolvedMotionSchema.optional(),
    placement: resolvedOverlayPlacementSchema.optional(),
});
export const resolvedLightRaysBlockSchema = z.object({
    kind: z.literal('light-rays'),
    ...resolvedBlockBaseShape,
    count: z.number().int().min(1).max(400),
    angle: z.number().min(-90).max(90).optional(),
    thickness: z.number().min(0.01).max(1).optional(),
    softness: z.number().min(0).max(1).optional(),
    feather: z.number().min(0).max(0.5).optional(),
    color: z.string().min(1),
    intensity: z.number().min(0).max(1).optional(),
    blend: z.enum(['screen', 'plus-lighter']).optional(),
    entrance: animationRef,
    entranceDurationFrames: z.number().int().min(1).optional(),
    motion: resolvedMotionSchema.optional(),
    placement: resolvedOverlayPlacementSchema.optional(),
});
export const resolvedStaggeredListBlockSchema = z.object({
    kind: z.literal('staggered-list'),
    ...resolvedBlockBaseShape,
    lead: resolvedTextElementSchema.optional(),
    items: z.array(resolvedTextElementSchema),
    trail: resolvedTextElementSchema.optional(),
    // 0 is a legal, AUTHORED value: `beatsPerItem: 0` (ins-outs-1) means "no
    // stagger — every element enters at frame 0". `min(1)` forced the compiler to
    // store a lie.
    beatsPerItemFrames: z.number().int().min(0),
    reveal: z.enum(['accumulate', 'sequential']).optional(),
    numbered: z.boolean().optional(),
    hAlign: z.enum(['left', 'center', 'right']).optional(),
    placement: resolvedOverlayPlacementSchema.optional(),
});
export const resolvedTextBlockSchema = z.object({
    kind: z.literal('text'),
    ...resolvedBlockBaseShape,
    text: z.string(),
    typeStyle: resolvedTypeStyleSchema,
    entrance: animationRef,
    entranceDurationFrames: z.number().int().min(1).optional(),
    entranceDelayFrames: z.number().int().min(0).optional(),
    motion: resolvedMotionSchema.optional(),
    container: z.enum(['none', 'pill', 'button', 'plate']).optional(),
    placement: resolvedOverlayPlacementSchema.optional(),
});
const mediaOverlayCornerSchema = z.enum(['tl', 'tr', 'bl', 'br']);
export const resolvedMediaOverlayPlacementSchema = z.union([
    z.literal('full-bleed'),
    z.object({
        kind: z.literal('corner'),
        corner: mediaOverlayCornerSchema,
        sizeRatio: z.number().min(0).max(1),
    }),
    z.object({
        kind: z.literal('rect'),
        x: z.number().min(0).max(1),
        y: z.number().min(0).max(1),
        w: z.number().min(0).max(1),
        h: z.number().min(0).max(1),
    }),
]);
export const resolvedMediaOverlayKenBurnsSchema = z.object({
    from: z.enum(['center', 'left', 'right']),
    zoomFrom: z.number().min(0),
    zoomTo: z.number().min(0),
});
export const resolvedMediaOverlayLabelSchema = z.object({
    text: z.string(),
    typeStyle: resolvedTypeStyleSchema,
    corner: mediaOverlayCornerSchema,
});
export const resolvedMediaOverlayBlockSchema = z.object({
    kind: z.literal('media-overlay'),
    ...resolvedBlockBaseShape,
    clip: resolvedMediaClipSchema,
    placement: resolvedMediaOverlayPlacementSchema,
    fit: z.enum(['cover', 'contain', 'contain-blur']),
    kenBurns: resolvedMediaOverlayKenBurnsSchema.optional(),
    // Ken Burns folded in. Present = it supersedes `kenBurns`.
    motion: resolvedMotionSchema.optional(),
    label: resolvedMediaOverlayLabelSchema.optional(),
});
export const resolvedInfoCardLayoutSchema = z.enum([
    'centered',
    'left-aligned',
    'stacked',
]);
export const resolvedInfoCardBackgroundSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('solid'), color: z.string().min(1) }),
    z.object({
        kind: z.literal('gradient'),
        from: z.string().min(1),
        to: z.string().min(1),
        angle: z.number().optional(),
    }),
]);
export const resolvedInfoCardBlockSchema = z.object({
    offerCard: resolvedOfferCardSchema.optional(),
    kind: z.literal('info-card'),
    ...resolvedBlockBaseShape,
    layout: resolvedInfoCardLayoutSchema,
    headline: z
        .object({ text: z.string(), typeStyle: resolvedTypeStyleSchema })
        .optional(),
    items: z
        .object({
        texts: z.array(z.string()),
        typeStyle: resolvedTypeStyleSchema,
    })
        .optional(),
    price: z
        .object({
        value: z.string(),
        currency: z.string(),
        typeStyle: resolvedTypeStyleSchema,
    })
        .optional(),
    cta: z
        .object({
        text: z.string(),
        url: z.string().optional(),
        typeStyle: resolvedTypeStyleSchema,
    })
        .optional(),
    logo: z
        .object({
        url: z.string().min(1),
        position: z.enum(['top', 'bottom']),
        size: z.enum(['badge', 'hero']).optional(),
        widthRatio: z.number().min(0).max(1).optional(),
    })
        .optional(),
    background: resolvedInfoCardBackgroundSchema,
    accent: z
        .object({ color: z.string().min(1), onColor: z.string().min(1) })
        .optional(),
    entrance: animationRef,
    entranceDurationFrames: z.number().int().min(1).optional(),
    motion: resolvedMotionSchema.optional(),
    placement: resolvedOverlayPlacementSchema.optional(),
});
// ── group (resolved) ────────────────────────────────────────────────
// `repeat` is expanded at compile time, so the resolved union is stack/text/bar.
export const resolvedFrameSchema = z.object({
    // An image painted as this surface, blurred. See ResolvedFrame.backdropUrl.
    backdropUrl: z.string().min(1).optional(),
    background: z.string().min(1).optional(),
    width: z.number().min(0).optional(),
    height: z.number().min(0).optional(),
    radius: z.number().min(0).optional(),
    borderColor: z.string().min(1).optional(),
    borderWidth: z.number().min(0).optional(),
    center: z.boolean().optional(),
    paddingX: z.number().min(0).optional(),
    paddingY: z.number().min(0).optional(),
    shadow: z.string().min(1).optional(),
    tail: z.enum(['bottom-left', 'none']).optional(),
});
const resolvedNodeAlignSchema = z.enum(['start', 'center', 'end', 'stretch']);
const resolvedNodeJustifySchema = z.enum([
    ...resolvedNodeAlignSchema.options,
    'space-between',
]);
const resolvedNodeWidthSchema = z.enum(['fill', 'hug']);
// One baked appearance swap (see ResolvedNodeState). Emitted only by a
// `repeat` carrying `active` / `past`; every other resolved node omits it.
export const resolvedNodeStateSchema = z.object({
    fromFrame: z.number().int().min(0),
    typeStyle: resolvedTypeStyleSchema.optional(),
    container: resolvedFrameSchema.optional(),
    opacity: z.number().min(0).max(1).optional(),
});
const resolvedNodeBaseShape = {
    id: z.string().min(1),
    container: resolvedFrameSchema.optional(),
    entrance: animationRef.optional(),
    entranceFrame: z.number().int().min(0).optional(),
    entranceDurationFrames: z.number().int().min(1).optional(),
    exitFrame: z.number().int().min(0).optional(),
    motion: resolvedMotionSchema.optional(),
    stateStyles: z.array(resolvedNodeStateSchema).min(1).optional(),
};
export const resolvedTextNodeSchema = z.object({
    kind: z.literal('text'),
    ...resolvedNodeBaseShape,
    text: z.string(),
    typeStyle: resolvedTypeStyleSchema,
    align: z.enum(['left', 'center', 'right']).optional(),
});
export const resolvedIconNodeSchema = z.object({
    kind: z.literal('icon'),
    ...resolvedNodeBaseShape,
    glyph: iconGlyphRef,
    size: z.number().positive(),
    color: z.string().min(1),
});
export const resolvedLightNodeSchema = z.object({
    kind: z.literal('light'),
    ...resolvedNodeBaseShape,
    color: z.string().min(1),
    size: z.number().positive(),
    falloff: z.number().positive().max(2),
    intensity: z.number().min(0).max(1),
});
// The mark, resolved: a concrete (presigned) url and a concrete width.
export const resolvedLogoNodeSchema = z.object({
    kind: z.literal('logo'),
    ...resolvedNodeBaseShape,
    url: z.string().min(1),
    width: z.number().positive().max(1),
    /** Height cap as a fraction of the FRAME height; absent = uncapped. */
    maxHeight: z.number().positive().max(1).optional(),
});
export const resolvedBarNodeSchema = z.object({
    kind: z.literal('bar'),
    ...resolvedNodeBaseShape,
    progress: z.union([z.literal('elapsed'), z.number().min(0).max(1)]),
    height: z.number().positive(),
    radius: z.number().min(0),
    trackColor: z.string().min(1),
    fillColor: z.string().min(1),
    glow: z.boolean(),
    tipDot: z.boolean(),
});
export const resolvedNodeSchema = z.lazy(() => z.discriminatedUnion('kind', [
    resolvedTextNodeSchema,
    resolvedIconNodeSchema,
    resolvedLogoNodeSchema,
    resolvedBarNodeSchema,
    resolvedLightNodeSchema,
    z.object({
        kind: z.literal('stack'),
        ...resolvedNodeBaseShape,
        direction: z.enum(['v', 'h']),
        gap: z.number().min(0).optional(),
        width: resolvedNodeWidthSchema.optional(),
        align: resolvedNodeAlignSchema.optional(),
        justify: resolvedNodeJustifySchema.optional(),
        children: z.array(resolvedNodeSchema),
    }),
]));
export const resolvedGroupBlockSchema = z.object({
    kind: z.literal('group'),
    ...resolvedBlockBaseShape,
    root: resolvedNodeSchema,
    placement: resolvedOverlayPlacementSchema.optional(),
});
export const resolvedBlockSchema = z.discriminatedUnion('kind', [
    resolvedMediaTrackBlockSchema,
    resolvedSolidBlockSchema,
    resolvedPanelStackBlockSchema,
    resolvedStaggeredListBlockSchema,
    resolvedTextBlockSchema,
    resolvedMediaOverlayBlockSchema,
    resolvedInfoCardBlockSchema,
    resolvedRectBlockSchema,
    resolvedLightRaysBlockSchema,
    resolvedGroupBlockSchema,
]);
// Recursive Resolved region tree — same shape rules as TemplateRegion.
const resolvedLeafRegionSchema = z.object({
    kind: z.literal('leaf'),
    id: z.string().min(1),
    spine: z.array(resolvedBlockSchema),
    overlays: z.array(resolvedBlockSchema),
});
export const resolvedRegionSchema = z.lazy(() => z.discriminatedUnion('kind', [
    resolvedLeafRegionSchema,
    z.object({
        kind: z.literal('split'),
        axis: z.enum(['h', 'v']),
        children: z.array(z.object({
            ratio: z.number().min(0),
            region: resolvedRegionSchema,
        })),
    }),
]));
export const resolvedMusicTrackSchema = z.object({
    trackId: z.string().min(1),
    url: z.string().min(1),
    volume: z.number().min(0).max(1),
    bpm: z.number().min(0).optional(),
});
export const resolvedNarrationSchema = z.discriminatedUnion('source', [
    z.object({
        source: z.literal('tts'),
        url: z.string().min(1),
        startFrame: z.number().int().min(0),
        durationInFrames: z.number().int().min(1),
        volume: z.number().min(0).max(1).optional(),
    }),
    z.object({
        source: z.literal('clip'),
        clipRef: z.string().min(1),
        lift: z.literal(true),
    }),
]);
export const resolvedCaptionsPageSchema = z.object({
    fromFrame: z.number().int().min(0),
    toFrame: z.number().int().min(0),
    text: z.string(),
    typeStyle: resolvedTypeStyleSchema,
});
export const resolvedCaptionsSchema = z.object({
    pages: z.array(resolvedCaptionsPageSchema),
    typeStyle: resolvedTypeStyleSchema,
});
export const renderDocSchema = z.object({
    premiumComposition: z
        .custom((value) => {
        try {
            return (!!value &&
                validatePremiumComposition(value).length === 0);
        }
        catch {
            return false;
        }
    })
        .optional(),
    schemaVersion: z.literal(2),
    videoId: z.string().min(1),
    templateDocId: z.string().min(1),
    fps: z.number().int().positive(),
    dimensions: resolvedDimensionsSchema,
    durationInFrames: z.number().int().min(1),
    orientation: renderOrientationSchema,
    root: resolvedRegionSchema,
    globals: z.object({
        audio: z.object({
            music: resolvedMusicTrackSchema.optional(),
            narration: resolvedNarrationSchema.optional(),
        }),
        captions: resolvedCaptionsSchema.optional(),
        theme: themeSchema.optional(),
    }),
});
//# sourceMappingURL=schemas.js.map